/**
 * Packstuecke auf dem Geraet (KONZEPT.md §5.2, ADR 0012).
 *
 * Den Inhalt eines gebuchten Packstuecks leitet der Server beim Eintreffen
 * ab. Bis dahin -- offline womoeglich Stunden -- nimmt das Geraet diese
 * Ableitung vorweg, mit derselben Regel (`inhaltBuchung` aus
 * `@thw/core-domain`). Geht die Gitterbox offline in den Einsatz, steht der
 * Leitungsroller darin sofort auch als „im Einsatz“ da.
 *
 * Die vorweggenommenen Buchungen landen nirgends: nicht in der Warteschlange,
 * nicht im Log. Sie gehen nur in die Rechnung ein. Die echten bringt der
 * Abgleich vom Server.
 */

import type { Buchung, Lagerort, Zustand } from "@thw/contracts";
import {
  inhaltBuchung,
  MAX_PACKSTUECK_TIEFE,
  sortiereBuchungen,
  teilNach,
  ziehtInhaltMit,
  type InhaltTeil,
  type StatusRegeln,
} from "@thw/core-domain";
import type { Bestand } from "./bestand.js";

/**
 * Die abgeleiteten Buchungen fuer den Inhalt wartender Packstueck-Buchungen,
 * je Artikel.
 *
 * Was im Packstueck ist, ergibt sich aus dem Zustand: alles, was dem Lagerort
 * des Packstuecks zugeordnet ist. Was davon mitgeht, sagt `ziehtInhaltMit` --
 * bei einer Bewegung alles ausser dem, was fuer sich allein verloren ist, bei
 * einem archivierenden Status alles, was da ist (ADR 0013 §10, ADR 0016).
 * Liegt darin wieder ein Packstueck, zieht auch dessen Inhalt mit -- so tief
 * wie auf dem Server.
 *
 * Die wartenden Buchungen werden nacheinander vorweggenommen, wie sie gelten:
 * Wird die Kiste offline als verloren gemeldet und gleich wieder
 * zurueckgenommen, ist ihr Inhalt danach auch wieder da.
 *
 * `zuletztAbgeleitet` sind die Artikel, deren letzte Buchung im Log eine
 * abgeleitete ist -- ist so einer archiviert, ging er mit der Kiste verloren.
 */
export function inhaltVorwegnehmen(
  wartende: readonly Buchung[],
  lagerorte: Iterable<Lagerort>,
  zustaende: ReadonlyMap<string, Zustand>,
  regeln: StatusRegeln,
  zuletztAbgeleitet: ReadonlySet<string> = new Set(),
): Map<string, Buchung[]> {
  const ortDesPackstuecks = new Map<string, string>();
  for (const l of lagerorte) {
    if (l.artikel_id !== null) ortDesPackstuecks.set(l.artikel_id, l.id);
  }

  // Wie jeder Teil gerade dasteht -- nach jeder vorweggenommenen Buchung neu.
  const teile = new Map<string, InhaltTeil>();
  const teilVon = (artikel_id: string): InhaltTeil =>
    teile.get(artikel_id) ?? {
      archiviert: zustaende.get(artikel_id)?.archiviert ?? false,
      zuletzt_abgeleitet: zuletztAbgeleitet.has(artikel_id),
    };

  const ergebnis = new Map<string, Buchung[]>();
  const ableiten = (ausloeserin: Buchung, tiefe: number): void => {
    if (tiefe >= MAX_PACKSTUECK_TIEFE) return;
    const ort = ortDesPackstuecks.get(ausloeserin.artikel_id);
    if (ort === undefined) return;
    for (const [artikel_id, z] of zustaende) {
      if (z.lagerort_id !== ort || artikel_id === ausloeserin.artikel_id) continue;
      if (!ziehtInhaltMit(ausloeserin, teilVon(artikel_id), regeln)) continue;
      const abgeleitet = inhaltBuchung(
        ausloeserin,
        artikel_id,
        ort,
        `${ausloeserin.id}/${artikel_id}`,
        regeln,
      );
      const liste = ergebnis.get(artikel_id);
      if (liste === undefined) ergebnis.set(artikel_id, [abgeleitet]);
      else liste.push(abgeleitet);
      teile.set(artikel_id, teilNach(abgeleitet, regeln));
      if (ortDesPackstuecks.has(artikel_id)) ableiten(abgeleitet, tiefe + 1);
    }
  };

  for (const b of sortiereBuchungen(wartende)) ableiten(b, 0);
  return ergebnis;
}

/**
 * Wo ein Packstueck gerade ist, als Zusatz zu seinem Lagerort: „in LKW-1“,
 * „im Einsatz — Pumpenstelle Nord“, „ausgeliehen“. `null`, wenn der Lagerort
 * kein Packstueck ist oder das Packstueck nie gebucht wurde.
 *
 * Damit beantwortet „Wo ist …?“ die Frage ganz: Der Roller liegt in GB-01 --
 * und GB-01 steht auf dem LKW.
 */
export function packstueckStandort(bestand: Bestand, lagerort_id: string): string | null {
  const artikel_id = bestand.lagerorte.get(lagerort_id)?.artikel_id ?? null;
  if (artikel_id === null) return null;
  const z = bestand.zustaende.get(artikel_id);
  if (z === undefined) return null;
  switch (z.verbleib) {
    case "im_lager": {
      const ort = z.lagerort_id === null ? undefined : bestand.lagerorte.get(z.lagerort_id);
      if (ort === undefined) return null;
      return ort.standardziel || ort.unbekannt ? `in „${ort.bezeichnung}“` : `in ${ort.kennung}`;
    }
    case "im_einsatz": {
      const stelle =
        z.einsatzstelle_id === null ? undefined : bestand.einsatzstellen.get(z.einsatzstelle_id);
      return stelle === undefined ? "im Einsatz" : `im Einsatz — ${stelle.bezeichnung}`;
    }
    case "ausgeliehen":
      return "ausgeliehen";
    case "nicht_erfasst":
      return null;
  }
}
